import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { FilterKeyType } from '@ocean/types/dist/filter'
import { PaginationUtils } from '@ocean/utils'
import {
  Card,
  ApiCardsDataResponseVirtualEventsEventSpecificValues,
  ApiCardsDataResponseVirtualEventsCategorySpecificValues,
} from 'types/card'
import { ApiAction, createApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import { getUncategorisedCategory } from 'modules/virtualClassrooms/common/helpers'
import {
  getEventLibraryData,
  getMyEventsData,
} from 'modules/virtualClassrooms/student/Events/api/eventsService'
import {
  EventLibraryFilter,
  EventLibrarySwitcher,
  EventLibraryFilterItem,
  MyEventsPeriodFilter,
  MyEventsFilterItem,
  EventLibraryFilterItems,
  MyEventsFilterItems,
} from 'modules/virtualClassrooms/student/Events/types'
import {
  EventsState,
  ApiVirtualEventsListCategoriesResponse,
  ApiMyEventsListResponse,
} from './types'

const initialState: EventsState = {
  eventLibraryData: {},
  eventLibraryFilters: {
    filter: { keyType: FilterKeyType.Number, value: EventLibraryFilter.All },
    isEventView: { keyType: FilterKeyType.Number, value: EventLibrarySwitcher.Events },
    searchText: { keyType: FilterKeyType.String },
  },
  myEventsData: {},
  myEventsFilters: {
    periodFilter: { keyType: FilterKeyType.Number, value: MyEventsPeriodFilter.Future },
    searchText: { keyType: FilterKeyType.String },
  },
}

const eventsSlice = createSlice({
  name: 'virtualClassroomStudentEvents',
  initialState,
  reducers: {
    eventLibraryDataLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiVirtualEventsListCategoriesResponse>>
    ) {
      state.eventLibraryData = action.payload
    },
    clearEventLibrary(state) {
      state.eventLibraryData = initialState.eventLibraryData
    },
    setEventLibraryFilters(state, action: PayloadAction<EventLibraryFilterItem>) {
      const { name, value } = action.payload
      state.eventLibraryFilters[name].value = value
    },
    myEventsDataLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiMyEventsListResponse>>
    ) {
      state.myEventsData = action.payload
    },
    clearMyEvents(state) {
      state.myEventsData = initialState.myEventsData
    },
    setMyEventsFilters(state, action: PayloadAction<MyEventsFilterItem>) {
      const { name, value } = action.payload
      state.myEventsFilters[name].value = value
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  eventLibraryDataLoaded,
  setEventLibraryFilters,
  myEventsDataLoaded,
  setMyEventsFilters,
  clearEventLibrary,
  clearMyEvents,
} = eventsSlice.actions

export { setEventLibraryFilters, setMyEventsFilters, clearEventLibrary, clearMyEvents }
export default eventsSlice.reducer

export const getEventsPageItems = createSelector(
  (events: Card[] | undefined, currentPageId: number, currentPageSize: number) => ({
    events,
    currentPageId,
    currentPageSize,
  }),
  ({ events, currentPageId, currentPageSize }) =>
    PaginationUtils.getPageItems<Card>(events, currentPageId, currentPageSize)
)

export const addUncategorisedCategory = createSelector(
  (categories: Card[] | undefined, events: Card[] | undefined) => ({
    categories,
    events,
  }),
  // TODO: Find a better way to handle a few default parameters
  ({ categories, events = [] }) => {
    if (!categories) return undefined

    const numberOfUncategorisedEvents: number = events.reduce(
      (result: number, { typeSpecificValues }: Card) => {
        const { categoryId } =
          typeSpecificValues as ApiCardsDataResponseVirtualEventsEventSpecificValues
        const isEventUncategorised = categoryId === null
        return isEventUncategorised ? result + 1 : result
      },
      0
    )
    const UNCATEGORISED_CATEGORY = getUncategorisedCategory()
    const typeSpecificValues =
      UNCATEGORISED_CATEGORY.typeSpecificValues as ApiCardsDataResponseVirtualEventsCategorySpecificValues
    typeSpecificValues.numberOfEvents = numberOfUncategorisedEvents

    return [...categories, UNCATEGORISED_CATEGORY]
  }
)

export const loadEventLibraryData = (
  filters: EventLibraryFilterItems,
  categoryId?: Guid
): ApiAction => {
  return createApiAction({
    apiCalls: {
      data: () => getEventLibraryData(filters, categoryId),
    },
    payloadAction: eventLibraryDataLoaded,
    shouldShowGlobalLoading: false,
    getResultToKeep: ({ virtualClassroomStudentEvents }) =>
      virtualClassroomStudentEvents.eventLibraryData.result,
  })
}

export const getMyEventsPageItems = createSelector(
  (events: Card[] | undefined, currentPageId: number, currentPageSize: number) => ({
    events,
    currentPageId,
    currentPageSize,
  }),
  ({ events, currentPageId, currentPageSize }) =>
    PaginationUtils.getPageItems<Card>(events, currentPageId, currentPageSize)
)

export const loadMyEventsData = (filters: MyEventsFilterItems): ApiAction => {
  return createApiAction({
    apiCalls: {
      data: () => getMyEventsData(filters),
    },
    payloadAction: myEventsDataLoaded,
    shouldShowGlobalLoading: false,
    getResultToKeep: ({ virtualClassroomStudentEvents }) =>
      virtualClassroomStudentEvents.myEventsData.result,
  })
}
